import { parseISO, isValid } from 'date-fns';
import {
  displayDate,
  displayDateTime,
  displayFrameworkDate,
  instantToZonedLocalInput,
} from '@vn-grc/shared';

/**
 * Format an ISO date string or Date as dd/MM/yyyy. Returns '--' on invalid input.
 *
 * The format itself is the shared `displayDate` (Sprint 62.50.1, W2), which a
 * server document printing what a page shows reads too.
 */
export function formatDate(iso: string | Date | null | undefined): string {
  if (!iso) return '--';
  try {
    const d = typeof iso === 'string' ? parseISO(iso) : iso;
    return isValid(d) ? (displayDate(d) ?? '--') : '--';
  } catch {
    return '--';
  }
}

/**
 * A framework record's date (`effective_date`, `effective_end`,
 * `framework_effective_date`, a framework activation's `effective_date`, a
 * sector overlay entry's effective and issue dates) at the precision the
 * record states it, never more:
 *
 * - `YYYY` reads `yyyy`;
 * - `YYYY-MM` reads `MM/yyyy`;
 * - `YYYY-MM-DD` reads `dd/MM/yyyy`, rearranged as text, never parsed
 *   through `Date`;
 * - anything else (a full instant) goes to {@link formatDate}.
 *
 * The first three are the shared `displayFrameworkDate`, which a server
 * document printing the same date reads too (gate E12.5 pass 3, P3-5).
 *
 * Returns '--' for an absent value or a month outside 01–12.
 *
 * I-1, gate E12.5 pass 1 (product-qa P2-1): a pack may date a record only as
 * precisely as its source does. GS1 dates GTS 2.0 "August 2017" with no day.
 * `formatDate("2017")` reads 01/01/2017, a day no source states, and
 * `new Date("2017").toLocaleDateString()` reads the last day of 2016 in a
 * browser west of UTC, because `new Date` takes a bare year as UTC midnight.
 *
 * Use it for every framework-record date a page shows; the schema
 * (`pack-schemas.ts`) holds `effective_date` as a string of any precision.
 * Not for the other dates a framework page shows (a deadline day, a guidance
 * date, a transition review's due date or completion instant): those are not
 * framework-record dates and go through {@link formatDate}. Where the date
 * stands as its own value, `components/regulatory/FrameworkDate.tsx` writes
 * this text inside `<time dateTime>`. A Vietnamese template that takes it
 * reads "từ {date}", never "từ ngày {date}": a month or a year is not a day
 * (gate E12.5 pass 2, engineering P3-G, product-qa P3-1, P3-4, P3-7).
 */
export function formatFrameworkDate(value: string | null | undefined): string {
  if (!value) return '--';
  return displayFrameworkDate(value) ?? formatDate(value);
}

/**
 * Format an ISO date string or Date as dd/MM/yyyy HH:mm. Returns '--' on invalid input.
 *
 * `timeZone` (Sprint 63.25.0) — an IANA zone to read the wall clock in, for a
 * record whose own timezone is known (an audit evidence pack stores the
 * organisation's). Absent, or not a zone the runtime knows, the browser's zone
 * is used, as before. Same output shape either way.
 */
export function formatDateTime(iso: string | Date | null | undefined, timeZone?: string): string {
  if (!iso) return '--';
  try {
    const d = typeof iso === 'string' ? parseISO(iso) : iso;
    if (!isValid(d)) return '--';
    if (timeZone) {
      const zoned = displayDateTime(d, timeZone);
      if (zoned) return zoned;
    }
    return displayDateTime(d) ?? '--';
  } catch {
    return '--';
  }
}

/**
 * A stored instant as a `<input type="datetime-local">` shows it: the
 * operator's own wall clock, `YYYY-MM-DDTHH:mm`. Empty for an absent or
 * unparseable value, so a form opened on a record never shows "Invalid Date".
 * Use it to OPEN a form on a record, and `toIsoInstant`
 * (`pages/admin/trust-evidence/controls.tsx`) to post the value back.
 *
 * Sprint 62.45.0 wrote it in the trust-evidence controls and Sprint 62.46.0
 * wrote it again in the AML case fields, with a narrower signature
 * (fix-sprint-20261003-0545 F18, R-43). This is the one copy.
 *
 * `timeZone` (Sprint 63.31.0) — an IANA zone whose wall clock the form shows,
 * for a form whose values are the organisation's own time (post it back with
 * `toIsoInstant(local, timeZone)`). Absent, or unknown to the runtime, the
 * browser's zone is used, as `formatDateTime` does.
 *
 * Anti-pattern: `value.slice(0, 16)`, which shows the UTC wall clock as
 * though it were local.
 */
export function instantToLocalInput(value: string | null | undefined, timeZone?: string): string {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  if (timeZone) {
    const zoned = instantToZonedLocalInput(parsed, timeZone);
    if (zoned) return zoned;
  }
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}T${pad(
    parsed.getHours(),
  )}:${pad(parsed.getMinutes())}`;
}
